import { ServiceLayout } from "@/components/ServiceLayout"
import { decentralizedIntelligenceFAQs } from "@/data/faqData"

const DecentralizedIntelligence = () => {
  return (
    <ServiceLayout
      title="Decentralized Intelligence"
      faqs={decentralizedIntelligenceFAQs}
      faqTitle="Decentralized Intelligence FAQs"
      faqSubtitle="Common questions about blockchain and smart contract solutions"
      description="Building trustless systems where smart contracts interact with oracle data feeds to automate complex multi-party transactions."
      heroImage="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&q=80&w=2600"
      features={[
        {
          title: "Smart Contract Audits",
          description:
            "Rigorous formal verification of Solidity/Rust/Vyper code to ensure 100% security before mainnet deployment.",
        },
        {
          title: "Oracle Integration for Real-World Data",
          description:
            "Connecting on-chain contracts with off-chain data sources (APIs, IoT sensors) via secure oracle networks like Chainlink.",
        },
        {
          title: "Tokenomics Design",
          description:
            "Architecting sustainable economic models for dApps and DAOs that incentivize long-term participation.",
        },
      ]}
      approach={{
        title: "Trust-Minimized Architecture",
        description:
          "Replacing expensive intermediaries with mathematically verified state machines.",
        image:
          "https://images.unsplash.com/photo-1642104704074-907c0698cbd9?auto=format&fit=crop&q=80&w=2600",
        content: [
          "We move beyond 'crypto' hype to implement mathematically verified state machines. Our derivation process focuses on the 'Trust Boundary' - analyzing where your organization relies on expensive intermediaries or slow manual reconciliation.",
          "We replace these friction points with deterministic Smart Contracts (Solidity/Rust) that execute autonomously. We integrate Chainlink Oracles to bring off-chain real-world data (IoT sensor readings, API data) onto the chain to trigger execution, ensuring that the system reacts to the real world.",
          "Security is paramount. We employ a 'Defense in Depth' strategy, utilizing Zero-Knowledge Proofs (zk-SNARKs) to validate transactions without revealing sensitive underlying data, which is critical for enterprise privacy.",
        ],
      }}
      benefits={[
        "Immutable Audit Trails",
        "Trustless Automation",
        "Zero-Knowledge Privacy",
        "Cross-Chain Interoperability",
        "DeFi Protocol Integration",
        "Enterprise-Grade Wallets",
      ]}
      process={{
        steps: [
          {
            title: "Protocol Architecture",
            description:
              "Defining the consensus mechanisms, token standards (ERC-20/721), and governance models for your decentralized application.",
          },
          {
            title: "Smart Contract Development",
            description:
              "Writing gas-optimized, secure code in Solidity or Rust, following industry-standard patterns (OpenZeppelin).",
          },
          {
            title: "Security Auditing",
            description:
              "Rigorous testing including static analysis, fuzzing, and third-party formal verification to eliminate vulnerabilities.",
          },
          {
            title: "Mainnet Deployment",
            description:
              "Orchestrating the launch including contract deployment, liquidity provisioning, and community governance setup.",
          },
        ],
      }}
      techStack={[
        {
          category: "Smart Contracts",
          items: ["Solidity", "Rust", "Hardhat", "Foundry"],
        },
        {
          category: "Web3 Integration",
          items: ["Ethers.js", "Wagmi", "Web3.js", "The Graph"],
        },
        {
          category: "Infrastructure",
          items: ["Infura", "Alchemy", "IPFS", "Chainlink"],
        },
      ]}
    />
  )
}

export default DecentralizedIntelligence
